import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BrandPillars = () => {
  const pillars = [
    {
      id: 1,
      title: "Authentic Recipes",
      subtitle: "Family Traditions Since 1987",
      description: `Our recipes have been passed down through three generations of Mediterranean chefs. Each dish tells the story of our family's culinary journey, preserving the authentic flavors and traditional cooking methods that make our food truly special.

Every spice blend, every marinade, and every cooking technique has been refined over decades to deliver the most authentic Mediterranean experience possible.`,
      image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      icon: "Heart",
      color: "primary",
      features: [
        "Three generations of family recipes",
        "Traditional cooking methods preserved",
        "Authentic Mediterranean spice blends",
        "Time-honored preparation techniques"
      ],
      cta: "Discover Our Heritage",
      link: "/our-story-heritage-authenticity"
    },
    {
      id: 2,
      title: "Fresh Daily",
      subtitle: "Premium Ingredients, Zero Compromise",
      description: `We source the finest ingredients every morning from trusted local suppliers and Mediterranean importers. Our commitment to freshness means we prepare everything in small batches throughout the day, ensuring every bite delivers peak flavor and nutrition.

From hand-picked herbs to premium cuts of meat, we never compromise on quality because we believe you deserve the very best.`,
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      icon: "Leaf",
      color: "trust",
      features: [
        "Daily fresh ingredient sourcing",
        "Local and Mediterranean suppliers",
        "Small batch preparation",
        "No preservatives or artificial additives"
      ],
      cta: "See Our Sourcing",
      link: "/our-story-heritage-authenticity"
    },
    {
      id: 3,
      title: "Made with Love",
      subtitle: "Craftsmanship in Every Dish",
      description: `Our chefs don't just cook—they craft each dish with passion, precision, and genuine care. Every kebab is hand-seasoned, every sauce is made from scratch, and every plate is assembled with the attention to detail that comes from true culinary artistry.

This dedication to craftsmanship ensures that every meal is not just food, but an experience that nourishes both body and soul.`,
      image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      icon: "Users",
      color: "accent",
      features: [
        "Hand-crafted preparation methods",
        "Made-from-scratch sauces and sides",
        "Artisanal attention to detail",
        "Passionate chef dedication"
      ],
      cta: "Meet Our Chefs",
      link: "/our-story-heritage-authenticity"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: 'bg-primary',
        text: 'text-primary',
        bgLight: 'bg-primary-50',
        border: 'border-primary-200'
      },
      trust: {
        bg: 'bg-trust',
        text: 'text-trust',
        bgLight: 'bg-trust-50',
        border: 'border-trust-200'
      },
      accent: {
        bg: 'bg-accent',
        text: 'text-accent',
        bgLight: 'bg-accent-50',
        border: 'border-accent-200'
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            What Makes Us Special
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto">
            Three core principles guide everything we do, ensuring every meal exceeds your expectations and honors our Mediterranean heritage
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="space-y-20">
          {pillars.map((pillar, index) => {
            const colors = getColorClasses(pillar.color);
            const isEven = index % 2 === 0;
            
            return (
              <div key={pillar.id} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                {/* Image Section */}
                <div className={`relative ${!isEven ? 'lg:col-start-2' : ''}`}>
                  <div className="relative h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-warm-lg">
                    <Image
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-text-primary/60 via-transparent to-transparent"></div>
                    
                    {/* Floating Icon */}
                    <div className={`absolute top-6 left-6 w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center shadow-warm`}>
                      <Icon name={pillar.icon} size={32} className="text-white" />
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className={`${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <div className="max-w-xl">
                    <div className={`inline-flex items-center space-x-2 ${colors.bgLight} ${colors.text} px-4 py-2 rounded-full font-cta font-semibold text-sm mb-6`}>
                      <Icon name={pillar.icon} size={16} />
                      <span>CORE PRINCIPLE</span>
                    </div>
                    
                    <h3 className="font-headline text-3xl lg:text-4xl font-bold text-text-primary mb-3">
                      {pillar.title}
                    </h3>
                    <h4 className="font-accent text-xl text-text-secondary mb-6">
                      {pillar.subtitle}
                    </h4>
                    
                    <div className="font-body text-lg text-text-secondary leading-relaxed mb-8">
                      <p className="mb-4">{pillar.description}</p>
                    </div>

                    {/* Features List */}
                    <div className="mb-8">
                      <h5 className="font-cta font-semibold text-text-primary mb-4">Key Highlights:</h5>
                      <div className="grid grid-cols-1 gap-3">
                        {pillar.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-3">
                            <div className={`w-6 h-6 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                              <Icon name="Check" size={14} className="text-white" />
                            </div>
                            <span className="font-body text-text-secondary">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={pillar.link}
                      className={`inline-flex items-center space-x-2 ${colors.bg} text-white font-cta font-semibold px-8 py-4 rounded-lg shadow-warm hover:opacity-90 transition-organic`}
                    >
                      <span>{pillar.cta}</span>
                      <Icon name="ArrowRight" size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-primary via-accent to-trust rounded-2xl p-12 text-white">
            <h3 className="font-headline text-3xl lg:text-4xl font-bold mb-4">
              Experience the Difference
            </h3>
            <p className="font-body text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              These aren't just promises—they're the foundation of every meal we serve. Taste the authenticity, freshness, and love in every bite.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/order-online-streamlined-experience"
                className="bg-white text-primary font-cta font-semibold px-8 py-4 rounded-lg hover:bg-primary-50 transition-organic"
              >
                <Icon name="ShoppingBag" size={20} className="mr-2" />
                Order Now
              </Link>
              <Link
                to="/interactive-menu-culinary-discovery"
                className="border-2 border-white text-white font-cta font-semibold px-8 py-4 rounded-lg hover:bg-white hover:text-primary transition-organic"
              >
                <Icon name="ChefHat" size={20} className="mr-2" />
                Explore Menu
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandPillars;